// apps/admin/src/presentation/merchants/components/StaffFormSheet.tsx
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  DEFAULT_STAFF_PERMISSIONS,
  STAFF_PERMISSION_LABELS,
  type StaffPermissions,
  type StaffRow,
} from "@/application/staff/types";

interface StaffFormSheetProps {
  open: boolean;
  staff: StaffRow | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (values: {
    name: string;
    email: string | null;
    isOwner: boolean;
    isActive: boolean;
    permissions: StaffPermissions;
    pin?: string;
  }) => void;
}

const PERMISSION_KEYS = Object.keys(
  STAFF_PERMISSION_LABELS,
) as (keyof StaffPermissions)[];

export function StaffFormSheet({
  open,
  staff,
  saving,
  onClose,
  onSubmit,
}: StaffFormSheetProps) {
  const isEdit = staff !== null;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isOwner, setIsOwner] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [permissions, setPermissions] = useState<StaffPermissions>(
    DEFAULT_STAFF_PERMISSIONS,
  );
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(staff?.name ?? "");
    setEmail(staff?.email ?? "");
    setIsOwner(staff?.isOwner ?? false);
    setIsActive(staff?.isActive ?? true);
    setPermissions(staff?.permissions ?? DEFAULT_STAFF_PERMISSIONS);
    setPin("");
    setError(null);
  }, [open, staff]);

  if (!open) return null;

  const pinValid = pin === "" || /^\d{4}$/.test(pin);

  function handleSubmit() {
    if (name.trim().length === 0) {
      setError("Name is required");
      return;
    }
    if (!isEdit && !/^\d{4}$/.test(pin)) {
      setError("A 4-digit PIN is required");
      return;
    }
    if (pin !== "" && !/^\d{4}$/.test(pin)) {
      setError("PIN must be exactly 4 digits");
      return;
    }
    onSubmit({
      name: name.trim(),
      email: email.trim() || null,
      isOwner,
      isActive,
      permissions,
      pin: pin === "" ? undefined : pin,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30">
      <div className="bg-card flex h-full w-full max-w-md flex-col shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-foreground text-base font-semibold">
            {isEdit ? "Edit staff member" : "Add staff member"}
          </h2>
          <button
            onClick={onClose}
            className="text-subtle-foreground hover:text-muted-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <label className="block">
            <span className="text-foreground text-sm font-medium">Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="border-border mt-1 w-full rounded-md border px-3 py-2 text-sm"
            />
          </label>

          <label className="block">
            <span className="text-foreground text-sm font-medium">
              Email (optional)
            </span>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border-border mt-1 w-full rounded-md border px-3 py-2 text-sm"
            />
          </label>

          <label className="block">
            <span className="text-foreground text-sm font-medium">
              {isEdit ? "New PIN (leave blank to keep current)" : "4-digit PIN"}
            </span>
            <input
              type="password"
              autoComplete="off"
              value={pin}
              inputMode="numeric"
              maxLength={4}
              onChange={(e) =>
                setPin(e.target.value.replace(/\D/g, "").slice(0, 4))
              }
              className="border-border mt-1 w-full rounded-md border px-3 py-2 font-mono text-sm tracking-widest"
              placeholder="••••"
            />
            {!pinValid && (
              <span className="mt-1 block text-xs text-red-600">
                PIN must be exactly 4 digits
              </span>
            )}
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isOwner}
              onChange={(e) => setIsOwner(e.target.checked)}
            />
            <span className="text-foreground text-sm">Owner</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span className="text-foreground text-sm">Active</span>
          </label>

          <fieldset className="space-y-2">
            <legend className="text-foreground text-sm font-medium">
              Permissions
            </legend>
            {PERMISSION_KEYS.map((key) => (
              <label key={key} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  // eslint-disable-next-line security/detect-object-injection
                  checked={permissions[key]}
                  onChange={(e) =>
                    setPermissions((prev) => ({
                      ...prev,
                      [key]: e.target.checked,
                    }))
                  }
                />
                <span className="text-foreground text-sm">
                  {/* eslint-disable-next-line security/detect-object-injection */}
                  {STAFF_PERMISSION_LABELS[key]}
                </span>
              </label>
            ))}
          </fieldset>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 border-t px-6 py-4">
          <button
            onClick={onClose}
            disabled={saving}
            className="border-border text-muted-foreground hover:border-muted-foreground/40 rounded-md border px-4 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !pinValid}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
